import React, { useState, useEffect, useRef, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const ASSET_BASE = 'https://processed.portal.soot.com/playground-static-assets/';

const EmailInput = memo(({
  email,
  isInputFocused,
  showInput,
  isTransitioning,
  onEmailChange,
  onFocus,
  onBlur,
  inputRef
}) => (
  <motion.div
    className="h-24 flex items-center justify-start"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{
      duration: 0.2,
      type: "spring",
      stiffness: 60,
      damping: 10,
      delay: 0.2
    }}
  >
    <div className="flex items-center gap-2 relative">
      <div className="relative">
        <motion.span
          className="text-xl text-[#666666] font-['ABC_Diatype_Rounded'] block"
          initial={{ opacity: 1, y: 0 }}
          animate={{
            y: (isInputFocused && email) ? -20 : 0,
            opacity: (isInputFocused && email) ? 0 : 1
          }}
          transition={{
            duration: 0.2,
            type: "spring",
            stiffness: 100,
            damping: 15
          }}
        >
          What is your
        </motion.span>

        <motion.span
          className="text-xl text-[#666666] font-['ABC_Diatype_Rounded'] absolute -right-0 top-0"
          initial={{ y: 20, opacity: 0 }}
          animate={{
            y: (isInputFocused && email) ? 0 : 20,
            opacity: (isInputFocused && email) ? 1 : 0
          }}
          transition={{
            duration: 0.2,
            type: "spring",
            stiffness: 100,
            damping: 15
          }}
        >
          My email is
        </motion.span>
      </div>

      <motion.div
        className="relative ml-0"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: showInput ? 1 : 0, scale: 1, y: 0 }}
        transition={{
          duration: 0.2,
          delay: 0.1,
          type: "spring",
          stiffness: 60,
          damping: 10
        }}
        style={{
          width: email.length > 4 ? `calc(${email.length}ch + 2rem)` : '6.5rem',
          transition: 'width 0.1s ease-out'
        }}
      >
        <input
          ref={inputRef}
          type="email"
          value={email}
          placeholder="email"
          onChange={onEmailChange}
          onFocus={onFocus}
          onBlur={onBlur}
          className={`w-full rounded bg-white text-xl text-[#666666] font-['ABC_Diatype_Rounded'] outline-none transition-opacity duration-600 placeholder-[#999999] ${
            showInput ? 'opacity-100' : 'opacity-0'
          } ${isTransitioning ? 'animate-fadeOut' : ''}`}
          style={{
            letterSpacing: '-0.01em',
            paddingTop: '0.125rem',
            paddingBottom: '0.125rem',
            paddingLeft: email ? '1rem' : '0.975rem',
            paddingRight: '0.375rem',
            transition: 'padding 0.1s ease-out'
          }}
        />
      </motion.div>

      <motion.span
        className="text-xl text-[#666666] font-['ABC_Diatype_Rounded']"
        initial={{ opacity: 0, x: -10 }}
        animate={{
          opacity: (!isInputFocused || !email) ? 1 : 0,
          x: (!isInputFocused || !email) ? 0 : -10
        }}
        transition={{
          duration: 0.2,
          type: "spring",
          stiffness: 100,
          damping: 15
        }}
      >
        ?
      </motion.span>
    </div>
  </motion.div>
));

const ContinueButton = memo(({
  show,
  onNext,
  email,
  isSending,
  isValidEmail
}) => (
  <motion.div
    className="h-40 flex flex-col items-center justify-start"
    initial={{ opacity: 0, y: 20 }}
    animate={{
      opacity: show ? 1 : 0,
      y: show ? 0 : 20
    }}
    transition={{
      duration: 0.2,
      type: "spring",
      stiffness: 60,
      damping: 10
    }}
  >
    <motion.button
      onClick={onNext}
      initial={{ opacity: 0, y: 10 }}
      animate={{ 
        opacity: email.trim() ? 1 : 0.5,
        y: 0 
      }}
      whileHover={{ 
        scale: (isValidEmail && !isSending) ? 1.05 : 1 
      }}
      className={`px-4 py-2 rounded-lg transition-colors duration-200 font-['ABC_Diatype_Rounded'] flex items-center gap-2
        ${(isValidEmail && !isSending)
          ? 'bg-white/20 hover:bg-white/30 text-[#666666] cursor-pointer'
          : 'bg-white/10 text-[#666666]/50 cursor-not-allowed'}`}
      disabled={!isValidEmail || isSending}
    >
      Continue
      <ArrowRight className="w-4 h-4" />
    </motion.button>
  </motion.div>
));

const ClaimMyAccount = ({ onFinish }) => {
  const [currentStep, setCurrentStep] = useState('email');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [email, setEmail] = useState('');
  const [showButton, setShowButton] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const emailRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowInput(true);
      emailRef.current?.focus();
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (email.length > 0) {
      const timer = setTimeout(() => {
        setShowButton(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [email]);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value.toLowerCase();
    setEmail(value);
  };

  const handleNext = async () => {
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      emailRef.current?.focus();
      return;
    }

    setEmailError('');
    setIsSending(true);
    // Simulate email sending with a delay
    setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentStep('confirmation');
        setIsSending(false);
      }, 600);
    }, 1500);
  };

  const ConfirmationScreen = () => {
    const [showCopied, setShowCopied] = useState(false);

    const handleCopyLink = () => {
      navigator.clipboard.writeText('waldo.soot.com');
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    };

    return (
      <motion.div 
        className="h-screen w-full bg-[#e9e9e9] flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="flex flex-col items-center space-y-8 px-4 max-w-lg">
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-medium text-[#666666] font-['ABC_Diatype_Rounded']">
              Check your email
            </h2>
            <p className="text-[#666666]/70 font-['ABC_Diatype_Rounded'] leading-relaxed">
              We've sent a confirmation link to
              <br />
              <span className="font-medium">{email}</span>
            </p>
            <p className="text-[#666666]/70 font-['ABC_Diatype_Rounded'] leading-relaxed">
              Click the link to access your account
            </p>
          </div>

          <motion.div 
            className="w-full bg-white/20 rounded-lg p-6 mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-lg font-medium text-[#666666] font-['ABC_Diatype_Rounded'] mb-4">
              Invite a friend to challenge themself in SOOTWORLD
            </h3>
            <button
              onClick={handleCopyLink}
              className="w-full flex items-center justify-between px-4 py-3 bg-white/20 hover:bg-white/30 rounded-lg text-[#666666] font-['ABC_Diatype_Rounded'] group transition-colors"
            >
              <span>waldo.soot.com</span>
              <div className="relative">
                <ArrowRight className="w-5 h-5 transform -rotate-45 group-hover:scale-110 transition-transform" />
                {showCopied && (
                  <motion.span
                    className="absolute -top-8 right-0 text-sm bg-black/70 text-white px-2 py-1 rounded"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                  >
                    Copied!
                  </motion.span>
                )}
              </div>
            </button>
          </motion.div>

          <div className="flex flex-col items-center gap-4">
            <button
              onClick={() => window.location.href = '/'}
              className="px-8 py-3 bg-[#f5f5f5] hover:bg-[#e5e5e5] transition-colors rounded-lg text-[#666666] font-['ABC_Diatype_Rounded']"
            >
              Play another Level
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  if (currentStep === 'confirmation') {
    return <ConfirmationScreen />;
  }

  return (
    <div className="w-full h-screen overflow-hidden bg-[#e9e9e9] relative">
      <div className="absolute inset-0 z-10">
        <div className="w-full h-full flex items-center justify-center">
          <div className="flex flex-col items-center min-h-[240px]">
            <EmailInput
              email={email}
              isInputFocused={isInputFocused}
              showInput={showInput}
              isTransitioning={isTransitioning}
              onEmailChange={handleEmailChange}
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => !email && setIsInputFocused(false)}
              inputRef={emailRef}
            />
            <ContinueButton
              show={showButton}
              onNext={handleNext}
              email={email}
              isSending={isSending}
              isValidEmail={validateEmail(email)}
            />
            {emailError && (
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-red-500 text-sm font-['ABC_Diatype_Rounded'] mt-2"
              >
                {emailError}
              </motion.span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClaimMyAccount;